import { Button, Icons, Input, Sheet, Stack, Switch, Typography } from '@healthinal/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { ScreeningCaseListItemDto } from '../../api/created/screening.ts';
import { ContentSection } from '../../components/ContentSection.tsx';
import { TruncateText } from '../../components/TruncateText.tsx';
import { PATIENT_TABLE_CELL_HEIGHT } from '../../theme.ts';
import { formatDate } from '../../utils/date.ts';
import { getScreeningCaseStatusName, getScreeningClientTypeName } from '../../utils/screening.ts';
import { PatientTable } from '../patients/components/tables/PatientTable.tsx';
import { CreateScreeningDialog } from './components/CreateScreeningDialog.tsx';
import { ExportScreeningDialog } from './components/ExportScreeningDialog.tsx';
import { ImportScreeningDialog } from './components/ImportScreeningDialog.tsx';
import { useScreeningCasesTable } from './components/useScreeningCasesTable.ts';

export const Route = createFileRoute('/screening/')({
  component: Screening,
});

function Screening() {
  const navigate = useNavigate();
  const [isCreateCaseOpen, setIsCreateCaseOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const {
    cases,
    isLoading,
    page,
    totalPages,
    activeOnly,
    search,
    handleActiveOnlyChange,
    handleSearchChange,
    handlePrevPage,
    handleNextPage,
  } = useScreeningCasesTable();

  const contentSectionRef = useRef(null);

  return (
    <>
      <ContentSection sx={{ overflowX: 'scroll' }} gap={2} ref={contentSectionRef}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography level="h1">Screening</Typography>
          <Stack direction="row" gap={1}>
            <Button onClick={() => setIsExportOpen(true)} endDecorator={<Icons.Download />}>
              Screening exportieren
            </Button>
            <Button onClick={() => setIsImportOpen(true)} endDecorator={<Icons.UploadFile />}>
              Screening importieren
            </Button>
            <Button onClick={() => setIsCreateCaseOpen(true)} endDecorator={<Icons.Add />}>
              Neuer Auftrag erfassen
            </Button>
          </Stack>
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Switch
            endDecorator="Nur Aktive Aufträge"
            checked={activeOnly}
            onClick={() => handleActiveOnlyChange(!activeOnly)}
          />
          <Input
            placeholder="Suche..."
            value={search}
            onChange={(event) => handleSearchChange(event.target.value)}
            sx={{ width: 260, maxWidth: '100%', flexShrink: 0 }}
          />
        </Stack>

        <Sheet sx={{ px: 2, py: 1, borderRadius: 5 }}>
          <PatientTable stickyHeader borderAxis="xBetween" noWrap hoverRow>
            <thead>
              <tr>
                <th style={{ width: 140 }}>Datum Erfassen</th>
                <th style={{ width: 140 }}>Auftraggeber</th>
                <th style={{ width: 160 }}>Gruppenbezeichnung</th>
                <th style={{ width: 110 }}>Termin</th>
                <th style={{ width: 90 }}>Test</th>
                <th style={{ width: 120, textAlign: 'center' }}>Anzahl der Personen</th>
                <th style={{ width: 100 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', height: PATIENT_TABLE_CELL_HEIGHT }}>
                    <Typography level="body-sm">Lade Daten...</Typography>
                  </td>
                </tr>
              ) : cases.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', height: PATIENT_TABLE_CELL_HEIGHT }}>
                    <Typography level="body-sm">Keine Daten vorhanden.</Typography>
                  </td>
                </tr>
              ) : (
                cases.map((item: ScreeningCaseListItemDto) => (
                  <tr
                    key={item.screeningCaseId}
                    onClick={() =>
                      navigate({
                        to: '/screening/$screeningId',
                        params: { screeningId: item.screeningCaseId },
                      })
                    }
                    style={{ cursor: 'pointer' }}>
                    <td>
                      <TruncateText>{formatDate(item.created)}</TruncateText>
                    </td>
                    <td>
                      <TruncateText>{getScreeningClientTypeName(item.clientType)}</TruncateText>
                    </td>
                    <td>
                      <TruncateText>
                        {item.individualCount > 1 && !item.groupName?.trim()
                          ? '-'
                          : item.groupName?.trim() || `${item.lastName ?? ''} ${item.firstName ?? ''}`.trim()}
                      </TruncateText>
                    </td>
                    <td>
                      <TruncateText>{formatDate(item.testDate)}</TruncateText>
                    </td>
                    <td>
                      <TruncateText>{item.desiredTest}</TruncateText>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <TruncateText>{item.individualCount}</TruncateText>
                    </td>
                    <td>
                      <TruncateText>{getScreeningCaseStatusName(item.status)}</TruncateText>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </PatientTable>

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
            <Typography level="body-sm">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span>Seite</span>
                <span translate="no" className="notranslate">
                  {totalPages === 0 ? 0 : page + 1}
                </span>
                <span>von</span>
                <span translate="no" className="notranslate">
                  {totalPages}
                </span>
              </span>
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button variant="outlined" disabled={page === 0 || isLoading} onClick={handlePrevPage}>
                Zurück
              </Button>
              <Button variant="outlined" disabled={page + 1 >= totalPages || isLoading} onClick={handleNextPage}>
                Weiter
              </Button>
            </Stack>
          </Stack>
        </Sheet>
      </ContentSection>

      <CreateScreeningDialog open={isCreateCaseOpen} onClose={() => setIsCreateCaseOpen(false)} />
      <ExportScreeningDialog open={isExportOpen} onClose={() => setIsExportOpen(false)} />
      <ImportScreeningDialog open={isImportOpen} onClose={() => setIsImportOpen(false)} />
    </>
  );
}
